import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "recordButton",
    "stopButton",
    "playButton",
    "discardButton",
    "submitButton",
    "submitButtonLabel",
    "consentCheckbox",
    "timer",
    "status",
    "audioPlayer",
    "waveform",
    "processingStatus"
  ];

  static values = {
    uploadUrl: String,
    deleteUrl: String,
    statusUrl: String,
    minDuration: { type: Number, default: 30 },
    maxDuration: { type: Number, default: 300 },
    voiceCloneStatus: String,
    messages: Object
  };

  connect() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.audioBlob = null;
    this.stream = null;
    this.recordingStartTime = null;
    this.timerInterval = null;

    this.updateUI();

    if (this.voiceCloneStatusValue === "pending" || this.voiceCloneStatusValue === "processing") {
      this.startPollingStatus();
    }
  }

  disconnect() {
    this.stopPollingStatus();
    this.cleanup();
  }

  async startRecording() {
    if (!this.hasConsentCheckboxTarget || !this.consentCheckboxTarget.checked) {
      this.showError(this.message("consent_before_recording"));
      return;
    }

    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error("[VoiceRecorder] getUserMedia not supported");
        this.showError(this.message("unsupported_browser"));
        return;
      }

      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = this.getSupportedMimeType();

      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.audioBlob = new Blob(this.audioChunks, { type: mimeType });
        this.onRecordingComplete();
      };

      this.mediaRecorder.start(1000);
      this.recordingStartTime = Date.now();
      this.startTimer();
      this.updateUIForRecording();
    } catch (error) {
      console.error("[VoiceRecorder] Error accessing microphone:", error);
      if (error.name === "NotAllowedError") {
        this.showError(this.message("microphone_denied"));
      } else if (error.name === "NotFoundError") {
        this.showError(this.message("microphone_missing"));
      } else {
        this.showError(`${this.message("microphone_error")} ${error.message}`);
      }
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
      this.stopTimer();
      this.cleanup();
    }
  }

  onRecordingComplete() {
    const duration = this.getRecordingDuration();

    if (duration < this.minDurationValue) {
      this.showError(this.message("recording_too_short").replace("%{seconds}", this.minDurationValue));
      this.audioBlob = null;
      this.updateUI();
      return;
    }

    const audioUrl = URL.createObjectURL(this.audioBlob);
    if (this.hasAudioPlayerTarget) {
      this.audioPlayerTarget.src = audioUrl;
    }

    this.updateUIForPlayback();
  }

  playRecording() {
    if (this.hasAudioPlayerTarget && this.audioBlob) {
      this.audioPlayerTarget.play();
    }
  }

  pauseRecording() {
    if (this.hasAudioPlayerTarget) {
      this.audioPlayerTarget.pause();
    }
  }

  deleteRecording() {
    this.audioBlob = null;
    this.audioChunks = [];
    if (this.hasAudioPlayerTarget) {
      this.audioPlayerTarget.src = "";
    }
    this.updateUI();
  }

  async submitRecording() {
    if (!this.audioBlob) {
      this.showError(this.message("no_recording"));
      return;
    }

    if (!this.hasConsentCheckboxTarget || !this.consentCheckboxTarget.checked) {
      this.showError(this.message("consent_before_submitting"));
      return;
    }

    const formData = new FormData();
    const extension = this.getFileExtension();
    formData.append("voice_sample", this.audioBlob, `voice_sample.${extension}`);
    formData.append("voice_clone_consent", "1");

    try {
      this.setSubmitting(true);

      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
          "Accept": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        this.showSuccess(this.message("upload_success"));
        this.voiceCloneStatusValue = "pending";
        this.startPollingStatus();
        this.updateUIForProcessing();
      } else {
        this.showError(data.error || this.message("upload_failed"));
      }
    } catch (error) {
      console.error("Upload error:", error);
      this.showError(this.message("upload_failed_retry"));
    } finally {
      this.setSubmitting(false);
    }
  }

  consentChanged() {
    this.syncConsentControls();
  }

  async deleteClonedVoice() {
    if (!confirm(this.message("delete_confirmation"))) {
      return;
    }

    try {
      const response = await fetch(this.deleteUrlValue, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        this.voiceCloneStatusValue = null;
        this.audioBlob = null;
        this.showSuccess(this.message("delete_success"));
        // Reload the page to reflect the deleted state
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const data = await response.json();
        this.showError(data.error || this.message("delete_failed"));
      }
    } catch (error) {
      console.error("Delete error:", error);
      this.showError(this.message("delete_failed_retry"));
    }
  }

  startTimer() {
    this.updateTimerDisplay(0);
    this.timerInterval = setInterval(() => {
      const elapsed = this.getRecordingDuration();
      this.updateTimerDisplay(elapsed);

      if (elapsed >= this.maxDurationValue) {
        this.stopRecording();
        this.showError(this.message("maximum_duration").replace("%{seconds}", this.maxDurationValue));
      }
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  updateTimerDisplay(seconds) {
    if (this.hasTimerTarget) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      this.timerTarget.textContent = `${mins}:${secs.toString().padStart(2, "0")}`;
    }
  }

  getRecordingDuration() {
    if (!this.recordingStartTime) return 0;
    return (Date.now() - this.recordingStartTime) / 1000;
  }

  getSupportedMimeType() {
    const types = ["audio/webm", "audio/ogg", "audio/mp4"];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return "audio/webm";
  }

  getFileExtension() {
    const mimeType = this.getSupportedMimeType();
    const extensions = {
      "audio/webm": "webm",
      "audio/ogg": "ogg",
      "audio/mp4": "m4a"
    };
    return extensions[mimeType] || "webm";
  }

  cleanup() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  updateUI() {
    const status = this.voiceCloneStatusValue;

    if (status === "ready") {
      this.showReadyState();
    } else if (status === "pending" || status === "processing") {
      this.updateUIForProcessing();
    } else if (status === "failed") {
      this.showFailedState();
    } else {
      this.showInitialState();
    }
  }

  showInitialState() {
    if (this.hasRecordButtonTarget) this.toggleElement(this.recordButtonTarget, true);
    if (this.hasStopButtonTarget) this.toggleElement(this.stopButtonTarget, false);
    if (this.hasPlayButtonTarget) this.toggleElement(this.playButtonTarget, false);
    if (this.hasDiscardButtonTarget) this.toggleElement(this.discardButtonTarget, false);
    if (this.hasSubmitButtonTarget) this.toggleElement(this.submitButtonTarget, false);
    if (this.hasProcessingStatusTarget) {
      this.processingStatusTarget.classList.add("hidden");
    }
    this.syncConsentControls();
  }

  updateUIForRecording() {
    if (this.hasRecordButtonTarget) this.toggleElement(this.recordButtonTarget, false);
    if (this.hasStopButtonTarget) this.toggleElement(this.stopButtonTarget, true);
    if (this.hasPlayButtonTarget) this.toggleElement(this.playButtonTarget, false);
    if (this.hasDiscardButtonTarget) this.toggleElement(this.discardButtonTarget, false);
    if (this.hasSubmitButtonTarget) this.toggleElement(this.submitButtonTarget, false);
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.message("recording");
      this.statusTarget.classList.remove("hidden");
    }
  }

  updateUIForPlayback() {
    if (this.hasRecordButtonTarget) this.toggleElement(this.recordButtonTarget, false);
    if (this.hasStopButtonTarget) this.toggleElement(this.stopButtonTarget, false);
    if (this.hasPlayButtonTarget) this.toggleElement(this.playButtonTarget, true);
    if (this.hasDiscardButtonTarget) this.toggleElement(this.discardButtonTarget, true);
    if (this.hasSubmitButtonTarget) this.toggleElement(this.submitButtonTarget, true);
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.message("recording_complete");
    }
  }

  updateUIForProcessing() {
    if (this.hasRecordButtonTarget) this.toggleElement(this.recordButtonTarget, false);
    if (this.hasStopButtonTarget) this.toggleElement(this.stopButtonTarget, false);
    if (this.hasPlayButtonTarget) this.toggleElement(this.playButtonTarget, false);
    if (this.hasDiscardButtonTarget) this.toggleElement(this.discardButtonTarget, false);
    if (this.hasSubmitButtonTarget) this.toggleElement(this.submitButtonTarget, false);
    if (this.hasProcessingStatusTarget) {
      this.processingStatusTarget.classList.remove("hidden");
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.message("processing");
      this.statusTarget.classList.remove("hidden");
    }
  }

  showReadyState() {
    if (this.hasRecordButtonTarget) this.toggleElement(this.recordButtonTarget, true);
    if (this.hasStopButtonTarget) this.toggleElement(this.stopButtonTarget, false);
    if (this.hasPlayButtonTarget) this.toggleElement(this.playButtonTarget, false);
    if (this.hasDiscardButtonTarget) this.toggleElement(this.discardButtonTarget, false);
    if (this.hasSubmitButtonTarget) this.toggleElement(this.submitButtonTarget, false);
    if (this.hasProcessingStatusTarget) {
      this.processingStatusTarget.classList.add("hidden");
    }
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("hidden");
    }
    this.syncConsentControls();
  }

  showFailedState() {
    if (this.hasRecordButtonTarget) this.toggleElement(this.recordButtonTarget, true);
    if (this.hasStopButtonTarget) this.toggleElement(this.stopButtonTarget, false);
    if (this.hasPlayButtonTarget) this.toggleElement(this.playButtonTarget, false);
    if (this.hasDiscardButtonTarget) this.toggleElement(this.discardButtonTarget, false);
    if (this.hasSubmitButtonTarget) this.toggleElement(this.submitButtonTarget, false);
    if (this.hasProcessingStatusTarget) {
      this.processingStatusTarget.classList.add("hidden");
    }
    this.syncConsentControls();
  }

  syncConsentControls() {
    const consented = this.hasConsentCheckboxTarget && this.consentCheckboxTarget.checked;

    if (this.hasRecordButtonTarget) this.recordButtonTarget.disabled = !consented;
    if (this.hasSubmitButtonTarget && !this.submitButtonTarget.classList.contains("hidden")) {
      this.submitButtonTarget.disabled = !consented;
    }
  }

  toggleElement(element, show) {
    if (element) {
      element.classList.toggle("hidden", !show);
    }
  }

  setSubmitting(submitting) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = submitting;
      if (this.hasSubmitButtonLabelTarget) {
        this.submitButtonLabelTarget.textContent = submitting ? this.message("uploading") : this.message("submit");
      }
    }
  }

  startPollingStatus() {
    this.stopPollingStatus();
    this.statusPollingInterval = setInterval(() => this.pollStatus(), 5000);
  }

  stopPollingStatus() {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
  }

  async pollStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: {
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.voiceCloneStatusValue = data.voice_clone_status;

        if (data.voice_clone_status === "ready") {
          this.stopPollingStatus();
          this.showSuccess(this.message("ready"));
          // Reload the page to show the server-rendered ready state
          window.location.reload();
        } else if (data.voice_clone_status === "failed") {
          this.stopPollingStatus();
          this.showError(data.voice_clone_error || this.message("clone_failed"));
          // Reload the page to show the server-rendered failed state with error
          window.location.reload();
        }
      }
    } catch (error) {
      console.error("Status polling error:", error);
    }
  }

  showError(message) {
    this.showNotification(message, "error");
  }

  message(key) {
    return this.messagesValue[key] || key;
  }

  showSuccess(message) {
    this.showNotification(message, "success");
  }

  showNotification(message, type) {
    const notification = document.createElement("div");
    notification.className = `fixed bottom-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
      type === "error"
        ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
        : "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
    }`;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 5000);
  }

  getCSRFToken() {
    const meta = document.querySelector("meta[name='csrf-token']");
    return meta ? meta.getAttribute("content") : "";
  }
}
